import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IState } from "store/types";
import { AdminActionButtons } from "./AdminActionButtons";
import { Autocomplete, TextField } from "@mui/material";
import { updateConfig } from "actions/config";
import { validateRegExMatch } from "lib/validation";
import { isEmpty } from "lodash";
import { Aligned, StyledChip } from "components/Layout";
import { styled } from "styled-components";
import { getUser } from "lib/Cookies";
import Spinner from "components/spinner";
import { AdminSectionTitle, AdminSectionDescription } from "./ApplicationSettings";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const ChipContainer = styled.div`
  display: flex;
  padding: 16px 16px 14px 16px;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 19px;
  align-self: stretch;
  border-radius: 4px;
  background: var(--neutral-100, #fafafa);
`;

export const AdministratorsList = () => {
  const emailRegex =
    "^(?=.{1,64}@.{1,255}$)([A-Za-z0-9][A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]*)@([A-Za-z0-9-]+)((\\.([A-Za-z0-9]){2,63}){1,126})$";

  const dispatch = useDispatch();

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [invalidEmailAddress, setInvalidEmailAddress] = useState("");

  // What we need from Redux
  const config = useSelector((state: IState) => state.config);
  const submittingChanges = useSelector((state: IState) => state.submittingChanges);
  const user = getUser();

  const [chipData, setChipData] = useState<string[]>(config.adminIds);

  const validateEmails = (adminIds: string[]) => {
    let errorFound = false;
    for (const email of adminIds) {
      if (!errorFound) {
        try {
          validateRegExMatch(email, emailRegex);
          setInvalidEmailAddress("");
        } catch (error: any) {
          setInvalidEmailAddress(email);
          errorFound = true;
        }
      }
    }
    return errorFound;
  };

  const handleAdd = useCallback((adminIds: string[]) => {
    const errorFound = validateEmails(adminIds);
    if (!errorFound) {
      setChipData(adminIds);
      setIsFormDirty(true);
    }
  }, []);

  const handleRemove = useCallback(
    (adminId: any) => () => {
      setInvalidEmailAddress(""); // remove any errors from the page
      setChipData((chips) => chips.filter((email: string) => email.toLowerCase() !== adminId.toLowerCase()));
      setIsFormDirty(true);
    },
    [],
  );

  const renderChips = useCallback(
    (value: any, getTagProps: any) => {
      return (
        <ChipContainer>
          {value.map((email: string, index: number) => (
            <div key={index}>
              <StyledChip
                label={email}
                {...getTagProps({ index })}
                // can't delete own email address
                onDelete={user && user.email.toLowerCase() === email.toLowerCase() ? undefined : handleRemove(email)}
              />
            </div>
          ))}
        </ChipContainer>
      );
    },
    [handleRemove, user],
  );

  const handleCancel = useCallback(() => {
    setChipData(config.adminIds);
    setIsFormDirty(false);
  }, [config.adminIds]);

  const handleSubmit = useCallback(() => {
    dispatch(updateConfig({ ...config, adminIds: chipData }));
    setIsFormDirty(false);
  }, [chipData, config, dispatch]);

  if (submittingChanges) {
    return <Spinner loading={submittingChanges} />;
  } else
    return (
      <>
        <AdminSectionTitle>Administrators</AdminSectionTitle>
        <AdminSectionDescription>
          Users with admin permissions can edit rules, restore rule versions and update template sites.
          <br />
          <Aligned>
            <ErrorOutlineIcon style={{ fontSize: "large", marginTop: -2, paddingRight: 2, color: "#252525" }} />
            Add users by entering their email address and pressing Enter. Click &apos;Save changes&apos; to apply.
          </Aligned>
        </AdminSectionDescription>
        <Autocomplete
          id="user-admin-autocomplete"
          multiple
          freeSolo
          value={chipData}
          options={[]}
          onChange={(_event, emails) => handleAdd(emails)}
          renderTags={(value, getTagProps) => renderChips(value, getTagProps)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label=""
              placeholder="Enter email address"
              helperText={
                isEmpty(invalidEmailAddress)
                  ? ""
                  : `'${invalidEmailAddress}' is not a correctly formatted email address`
              }
              error={!isEmpty(invalidEmailAddress)}
            />
          )}
        />
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        <AdminActionButtons
          onCancel={handleCancel}
          onSave={handleSubmit}
          canSave={!submittingChanges && isFormDirty && chipData.length > 0}
          isFormDirty={isFormDirty}
        />
      </>
    );
};
